import type { DataGridColumn, DataTableColumnSizing } from './data-table.types';

export interface ComputeColWidthsResult {
  colWidths: Record<string, string>;
  tableWidth: string;
  selectionWidth: string;
}

interface MeasureOptions {
  minWidth?: number;
  padding?: number;
  sortIconWidth?: number;
  fontScale?: number;
  headerFont?: string;
  dataFont?: string;
}

const ACTIONS_W = 48;
const SELECTION_W = 48;
/** Espacio extra para texto + gap + botón en columnas type link. */
const LINK_CELL_EXTRA = 56;
/** Ancho de una columna sin `width` declarado en modo `fixed`. */
const DEFAULT_FIXED_W = 150;
/** Mínimo general que conserva texto útil antes de aplicar elipsis. */
const DEFAULT_AUTO_MIN_W = 64;
const BUTTON_FONT = '600 14px "DM Sans", sans-serif';
const BUTTON_INLINE_PADDING = 26;
const BUTTON_GAP = 8;
const CELL_INLINE_PADDING = 28;

function getButtonColumnMinWidth(col: DataGridColumn): number {
  if (col.type !== 'button' || !col.buttonLabels?.length) return 0;

  const buttonsWidth = col.buttonLabels.reduce(
    (total, label) =>
      total + Math.ceil(measureText(label, BUTTON_FONT)) + BUTTON_INLINE_PADDING,
    0,
  );
  const gapsWidth = Math.max(0, col.buttonLabels.length - 1) * BUTTON_GAP;
  return buttonsWidth + gapsWidth + CELL_INLINE_PADDING;
}

let measureCanvas: HTMLCanvasElement | null = null;

/**
 * Ancho del texto. Con saltos de línea (`pre-line` / multilínea) usa la línea
 * más ancha, no la suma horizontal del string completo (canvas no parte por `\n`).
 */
function measureText(text: string, font: string): number {
  if (!measureCanvas) measureCanvas = document.createElement('canvas');
  const ctx = measureCanvas.getContext('2d');
  if (!ctx) return 100;
  ctx.font = font;
  const lines = text.split(/\r?\n/);
  let max = 0;
  for (const line of lines) {
    const w = ctx.measureText(line).width;
    if (w > max) max = w;
  }
  return max;
}

export function computeDataTableColumnWidths(params: {
  columns: readonly DataGridColumn[];
  rows: readonly Record<string, unknown>[];
  containerWidth: number;
  showActionsColumn: boolean;
  showSelectionColumn?: boolean;
  showLinkButtons?: boolean;
  sortingEnabled?: boolean;
  /** `fixed` toma el `width` de cada columna en vez de medir. Default: `auto`. */
  columnSizing?: DataTableColumnSizing;
  getCellText: (row: Record<string, unknown>, col: DataGridColumn) => string;
  options?: MeasureOptions;
}): ComputeColWidthsResult {
  const cols = params.columns;
  if (!cols.length || params.containerWidth <= 0) {
    return { colWidths: {}, tableWidth: '100%', selectionWidth: '0px' };
  }

  const isFixed = params.columnSizing === 'fixed';

  const MIN_WIDTH  = params.options?.minWidth ?? DEFAULT_AUTO_MIN_W;
  const PADDING    = params.options?.padding ?? 40;
  const SORT_ICON  = params.options?.sortIconWidth ?? 24;
  const FONT_SCALE = params.options?.fontScale ?? 1.12;
  const H_FONT     = params.options?.headerFont ?? '600 13px "DM Sans", sans-serif';
  const D_FONT     = params.options?.dataFont ?? '400 13px "DM Sans", sans-serif';
  const actionsW = params.showActionsColumn ? ACTIONS_W : 0;
  const selectionW = params.showSelectionColumn ? SELECTION_W : 0;
  const extraW = actionsW + selectionW;

  const natural: Record<string, number> = {};
  const minimum: Record<string, number> = {};
  for (const col of cols) {
    let columnMin = Math.max(1, Math.ceil(col.minWidth ?? MIN_WIDTH));
    if (
      col.type === 'link' &&
      col.linkKey &&
      params.showLinkButtons !== false
    ) {
      columnMin = Math.max(columnMin, MIN_WIDTH + LINK_CELL_EXTRA);
    }
    if (col.iconButtons?.length) {
      const buttonsWidth =
        col.iconButtons.length * 32 +
        Math.max(0, col.iconButtons.length - 1) * BUTTON_GAP +
        CELL_INLINE_PADDING;
      columnMin = Math.max(columnMin, buttonsWidth);
    }
    columnMin = Math.max(columnMin, getButtonColumnMinWidth(col));

    let max = measureText(col.header, H_FONT) * FONT_SCALE;
    if (params.sortingEnabled !== false && col.sortable !== false) max += SORT_ICON;
    for (const row of params.rows) {
      const w = measureText(params.getCellText(row, col), D_FONT) * FONT_SCALE;
      if (w > max) max = w;
    }
    let width = Math.max(MIN_WIDTH, Math.ceil(max) + PADDING);
    if (
      col.type === 'link' &&
      col.linkKey &&
      params.showLinkButtons !== false
    ) {
      width += LINK_CELL_EXTRA;
    }
    const preferredWidth = Math.max(columnMin, width);

    if (isFixed) {
      const fixedWidth = Math.max(
        col.width ?? DEFAULT_FIXED_W,
        columnMin,
      );
      natural[col.key] = col.truncate
        ? fixedWidth
        : Math.max(fixedWidth, preferredWidth);
      continue;
    }

    natural[col.key] = preferredWidth;
    minimum[col.key] = col.truncate ? columnMin : preferredWidth;
  }

  const available = params.containerWidth > 0 ? params.containerWidth - extraW : 0;
  const totalNat  = Object.values(natural).reduce((a, b) => a + b, 0);

  const final: Record<string, number> = {};
  let tableWidth: string;

  if (totalNat <= available) {
    if (isFixed) {
      // Los anchos declarados se respetan: el sobrante lo absorbe la última columna.
      for (const col of cols) final[col.key] = natural[col.key];
    } else {
      const extra = available - totalNat;
      for (const col of cols) {
        final[col.key] = natural[col.key] + Math.floor((natural[col.key] / totalNat) * extra);
      }
    }
    const assigned = Object.values(final).reduce((a, b) => a + b, 0);
    const leftover = available - assigned;
    if (leftover > 0) {
      const growColumns = isFixed
        ? cols.filter(col => (col.grow ?? 0) > 0)
        : [];
      const totalGrow = growColumns.reduce(
        (total, col) => total + (col.grow ?? 0),
        0,
      );

      if (totalGrow > 0) {
        let remaining = leftover;
        growColumns.forEach((col, index) => {
          const extra =
            index === growColumns.length - 1
              ? remaining
              : Math.floor((leftover * (col.grow ?? 0)) / totalGrow);
          final[col.key] += extra;
          remaining -= extra;
        });
      } else {
        const lastColumn = cols[cols.length - 1];
        if (lastColumn) final[lastColumn.key] += leftover;
      }
    }
    tableWidth = '100%';
  } else {
    const totalMin = isFixed
      ? 0
      : cols.reduce((total, col) => total + minimum[col.key], 0);

    if (!isFixed && totalMin <= available) {
      const flexibleWidth = totalNat - totalMin;
      const targetFlexibleWidth = available - totalMin;
      let assigned = 0;

      for (const col of cols) {
        const columnFlexibleWidth = natural[col.key] - minimum[col.key];
        const fittedFlexibleWidth =
          flexibleWidth > 0
            ? Math.floor(
                (columnFlexibleWidth / flexibleWidth) * targetFlexibleWidth,
              )
            : 0;
        final[col.key] = minimum[col.key] + fittedFlexibleWidth;
        assigned += final[col.key];
      }

      let leftover = available - assigned;
      for (const col of cols) {
        if (leftover <= 0) break;
        if (final[col.key] >= natural[col.key]) continue;
        final[col.key] += 1;
        leftover -= 1;
      }
      tableWidth = '100%';
    } else {
      for (const col of cols) {
        final[col.key] = isFixed ? natural[col.key] : minimum[col.key];
      }
      const contentWidth = Object.values(final).reduce(
        (total, width) => total + width,
        0,
      );
      tableWidth = `${contentWidth + extraW}px`;
    }
  }

  const colWidths: Record<string, string> = {};
  for (const col of cols) colWidths[col.key] = `${final[col.key]}px`;
  return { colWidths, tableWidth, selectionWidth: `${selectionW}px` };
}

